import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Activity, ShieldCheck, SkipForward } from 'lucide-react';
import { ProgressBar } from '../components/Onboarding/ProgressBar';
import { GenderSelectionScreen } from '../components/Onboarding/GenderSelectionScreen';
import { QuestionScreen } from '../components/Onboarding/QuestionScreen';
import { UnlockScreen } from '../components/Onboarding/UnlockScreen';
import { useAuth } from '../contexts/AuthContext';
import { trackCriticalEvent } from '../utils/mixpanelTracking';
const questions = [{
  iconType: 'battery',
  question: 'Do their texts make you feel drained, like emotionally bankrupt?',
  hint: 'Detecting energy vampire patterns.',
  key: 'q2_feeling_drained'
}, {
  iconType: 'brain',
  question: 'Ever catch yourself thinking "Wait, are they gaslighting me again?"',
  hint: 'Analyzing reality distortion fields.',
  key: 'q3_suspect_gaslighting'
}, {
  iconType: 'bomb',
  question: 'Do tiny fights blow up like a full-on drama?',
  hint: 'Scanning for volatility markers.',
  key: 'q4_fights_blow_up'
}, {
  iconType: 'shield',
  question: "Do you apologize even when you're NOT wrong?",
  hint: 'Identifying submission conditioning.',
  key: 'q5_apologize_when_not_wrong'
}, {
  iconType: 'search',
  question: 'Curious what your ex really means behind the texts?',
  hint: 'Decoding subtextual manipulation.',
  key: 'q6_curious_ex_emojis'
}, {
  iconType: 'alert',
  question: "Wanna know if your ex is a disaster, or if it's just your imagination?",
  hint: 'Compiling final toxicity report.',
  key: 'q7_ex_disaster_imagination'
}];
// Answer text mapping for tracking
const ANSWER_TEXT = {
  yes: 'Yes, definitely',
  no: 'No, not really',
  skip: 'Skipped'
};
const GENDER_TEXT = {
  female: 'Female',
  male: 'Male',
  other: 'Other'
};
export function OnboardingFlow() {
  const navigate = useNavigate();
  const {
    user,
    updateUserProfile
  } = useAuth();
  const [currentStep, setCurrentStep] = useState(-1);
  const [gender, setGender] = useState<'female' | 'male' | 'other' | null>(null);
  const [answers, setAnswers] = useState<Array<'yes' | 'no' | 'skip'>>([]);
  const [showUnlock, setShowUnlock] = useState(false);
  const [hasTrackedCompletion, setHasTrackedCompletion] = useState(false);
  useEffect(() => {
    if (user) {
      const hasName = user.user_metadata?.firstName && user.user_metadata?.lastName;
      if (!hasName && user.user_metadata?.full_name) {
        const fullName = user.user_metadata.full_name || '';
        const nameParts = fullName.split(' ');
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';
        updateUserProfile({
          firstName,
          lastName,
          fullName
        }).catch(console.error);
      }
    }
  }, [user]);
  // Track onboarding completion when unlock screen is shown
  useEffect(() => {
    if (showUnlock && !hasTrackedCompletion && gender) {
      trackOnboardingCompletion();
      setHasTrackedCompletion(true);
    }
  }, [showUnlock, hasTrackedCompletion, gender]);
  /**
   * Track onboarding completion with all quiz answers
   */
  const trackOnboardingCompletion = async () => {
    try {
      // Build properties object with all answers
      const properties: Record<string, string> = {
        gender_choice: GENDER_TEXT[gender!]
      };
      // Add each question answer with its key
      answers.forEach((answer, index) => {
        if (index < questions.length) {
          properties[questions[index].key] = ANSWER_TEXT[answer];
        }
      });
      // Track the event using existing infrastructure
      await trackCriticalEvent('ToxicDetector Onboarding Completed', properties);
      console.log('✅ Onboarding completion tracked successfully');
    } catch (error) {
      console.error('❌ Failed to track onboarding completion:', error);
    }
  };
  const handleGenderSelect = (selectedGender: 'female' | 'male' | 'other') => {
    setGender(selectedGender);
    setTimeout(() => {
      setCurrentStep(0);
    }, 300);
  };
  const handleAnswer = (answer: 'yes' | 'no') => {
    const newAnswers = [...answers, answer];
    setAnswers(newAnswers);
    if (currentStep < questions.length - 1) {
      setTimeout(() => {
        setCurrentStep(currentStep + 1);
      }, 300);
    } else {
      setTimeout(() => {
        setShowUnlock(true);
      }, 300);
    }
  };
  const handleSkip = () => {
    const newAnswers = [...answers, 'skip'];
    setAnswers(newAnswers);
    if (currentStep < questions.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      setShowUnlock(true);
    }
  };
  const handleSkipToDashboard = async () => {
    // Mark onboarding as completed so returning users skip it
    try {
      await updateUserProfile({
        completedOnboarding: true
      });
    } catch (err) {
      console.error('Failed to mark onboarding complete:', err);
    }
    navigate('/dashboard');
  };
  const handleUnlock = async () => {
    // Mark onboarding as completed so returning users skip it
    try {
      await updateUserProfile({
        completedOnboarding: true
      });
    } catch (err) {
      console.error('Failed to mark onboarding complete:', err);
    }
    navigate('/dashboard');
  };
  const totalSteps = questions.length + 1;
  const currentProgress = currentStep + 2;
  return <div className="flex flex-col w-full min-h-screen bg-[#050505] text-white relative overflow-hidden font-sans" data-id="element-9509">
      {/* Grid Background Overlay */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(20,20,20,0.5)_1px,transparent_1px),linear-gradient(90deg,rgba(20,20,20,0.5)_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,#000_70%,transparent_100%)] pointer-events-none z-0" data-id="element-9510" />

      {/* Floating ambient glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-[#FF0000] rounded-full blur-[150px] opacity-[0.08] pointer-events-none z-0" data-id="element-9511" />

      <div className="max-w-lg mx-auto w-full relative z-10 flex flex-col min-h-screen" data-id="element-9512">
        <motion.header className="pt-6 pb-4 px-6" initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        ease: 'easeOut'
      }} data-id="element-9513">
          <div className="flex items-center justify-between" data-id="element-9514">
            <div className="flex items-center gap-3" data-id="element-9515">
              <motion.div whileHover={{
              x: -2
            }} whileTap={{
              scale: 0.95
            }} data-id="element-9516">
                <Link to="/" className="flex items-center justify-center w-10 h-10 border border-white/10 bg-white/5 hover:bg-white/10 hover:border-white/20 transition-all duration-200 backdrop-blur-sm" data-id="element-9517">
                  <ArrowLeft size={18} className="text-gray-300" data-id="element-9518" />
                </Link>
              </motion.div>
              <div className="flex flex-col" data-id="element-9519">
                <h1 className="text-sm font-bold text-white uppercase tracking-wider flex items-center gap-2" data-id="element-9520">
                  <Activity size={14} className="text-[#FF0000]" data-id="element-9521" />
                  ToxicDetector
                </h1>
                <span className="text-[10px] font-mono text-[#FF0000] tracking-[0.2em] uppercase opacity-80" data-id="element-9522">
                  System Active
                </span>
              </div>
            </div>

            {/* Skip to Dashboard Button */}
            <motion.button onClick={handleSkipToDashboard} className="flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 transition-all duration-200 backdrop-blur-sm group" whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} data-id="element-9523">
              <span className="text-xs font-mono text-gray-400 group-hover:text-white tracking-wider uppercase" data-id="element-9524">
                Skip
              </span>
              <SkipForward size={14} className="text-gray-400 group-hover:text-white" data-id="element-9525" />
            </motion.button>
          </div>
        </motion.header>

        <main className="px-6 flex-1 flex flex-col" data-id="element-9526">
          <AnimatePresence mode="wait" data-id="element-9527">
            {!showUnlock && <motion.div key="progress" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.3
          }} className="mb-8" data-id="element-9528">
                <ProgressBar current={currentProgress} total={totalSteps} data-id="element-9529" />
              </motion.div>}
          </AnimatePresence>

          <div className="flex-1 flex flex-col justify-center pb-12" data-id="element-9530">
            <AnimatePresence mode="wait" data-id="element-9531">
              {!showUnlock ? currentStep === -1 ? <GenderSelectionScreen key="gender-selection" onSelect={handleGenderSelect} onSkip={handleSkipToDashboard} data-id="element-9532" /> : <QuestionScreen key={`question-${currentStep}`} iconType={questions[currentStep].iconType} question={questions[currentStep].question} hint={questions[currentStep].hint} onAnswer={handleAnswer} onSkip={handleSkip} data-id="element-9533" /> : <UnlockScreen key="unlock" onUnlock={handleUnlock} data-id="element-9534" />}
            </AnimatePresence>
          </div>
        </main>
      </div>
    </div>;
}